/**
 * Écriture des noms de personnes — jumeau de `backend/src/lib/noms.js`.
 *
 * ⚠️ Les deux fichiers doivent rester IDENTIQUES sous cet en-tête (le front ne peut
 * pas importer le back) : toute règle se change dans les deux, `diff` à l'appui.
 * Ici, il sert à MONTRER à l'écran le nom affiché qui sera enregistré :
 * c'est le serveur qui fait foi et le recalcule à chaque enregistrement.
 * Décision du 05-10-2026 : « NOM Prénom » (DEWOLF Maxence).
 */

// Apostrophes (’ ‘ ʼ ´ ′ `) et tirets (‐ ‑ ‒ – − ) typographiques — tapés par
// iPhone / Mac, corrigés par Word ou collés — ramenés à « ' » et « - » : une
// seule forme, sinon « D’Angelo » ne correspond pas à « D'ANGELO ».
// Caractères invisibles parasites collés depuis Word ou Outlook (trait d'union
// conditionnel, espace sans largeur, marques de sens d'écriture…) : retirés, sinon
// « Du\u00ADpont » ≠ « Dupont ». PAS les liants ZWNJ/ZWJ (U+200C/U+200D), qui font
// partie de l'orthographe de noms persans, kurdes ou indiens. Retirés AVANT la
// recomposition : entre une lettre et son accent, ils l'empêchaient.
// Les « lettres » de remplissage coréennes, invisibles elles aussi, sont retirées.
const INVISIBLES = /[\u00AD\u061C\u115F\u1160\u180E\u200B\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\u3164\uFEFF\uFFA0]/g;
const espaces = (s) => String(s ?? '').replace(INVISIBLES, '').normalize('NFC')
  .replace(/[\u2018\u2019\u02BC\u00B4\u2032\u0060]/g, "'").replace(/[\u2010\u2011\u2012\u2013\u2212]/g, '-')
  .replace(/\s+/g, ' ').trim();

/** Nom nettoyé (espaces, apostrophes, tirets), sans changer la casse — pour comparer. */
export const nettoyerNom = espaces;

/** DE LA RUBIA FERNANDEZ */
export function nomEnMajuscules(s) {
  return espaces(s).toUpperCase();
}

/**
 * Jean-Marc, D'Angelo, Marina Florentina. Chaque mot est remis en forme (LIONEL,
 * mARIE, JEan, JeAn → Lionel, Marie, Jean, Jean) ; seules les majuscules internes
 * d'un prénom composé collé sont gardées, quand chaque partie a au moins 3 lettres
 * (AnaMaria).
 */
export function prenomEnForme(s) {
  return espaces(s)
    .split(/([ \-'])/)
    .map((part) => {
      if (!part || /^[ \-']$/.test(part)) return part;
      // Majuscules internes gardées SEULEMENT pour un prénom composé collé : parties
      // d'au moins 3 lettres. Nécessaire pour un prénom RÉEL de Zeus (« AnaMaria
      // Dariana ») ; contrepartie assumée : une faute du même dessin (« NatHalie »)
      // est gardée telle quelle et se corrige à la main. Tout le reste est remis en
      // forme — LIONEL, mARIE, JEan, JeAn → Lionel, Marie, Jean, Jean.
      // Appliquée deux fois, la règle donne le même résultat.
      if (/^\p{L}\p{Ll}{2,}(\p{Lu}\p{Ll}{2,})+$/u.test(part)) return part.charAt(0).toUpperCase() + part.slice(1);
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join('');
}

/**
 * Nom affiché, déduit de ce qui est connu : « NOM Prénom », ou le seul des deux
 * qui est rempli (« Kenny »). null seulement si AUCUN n'est rempli : l'appelant
 * garde alors le nom saisi à la main (robots, comptes techniques).
 */
export function nomAffiche(lastName, firstName) {
  const nom = espaces(lastName), prenom = espaces(firstName);
  if (nom && prenom) return `${nomEnMajuscules(nom)} ${prenomEnForme(prenom)}`;
  if (prenom) return prenomEnForme(prenom);
  if (nom) return nomEnMajuscules(nom);
  return null;
}

// Sans accents ni casse (ß = SS, comme après la mise en majuscules), tirets et
// apostrophes comme espaces : pour vérifier que
// « Jean Dupont » contient bien « DUPONT » (mot entier, pas « Martine » pour « MARTIN »).
// Seuls les ACCENTS sont retirés — diacritiques combinants, et points-voyelles /
// hamza arabes et hébreux (souvent omis à la saisie : احمد = أحمد) — pas les signes
// vocaliques des écritures indiennes ou thaïe, qui changent le nom (किरण ≠ करण).
// Les caractères « ignorables » (sélecteurs de variante, remplissages, liants) aussi.
// (Hébreu : points-voyelles seulement — le maqaf ־ reste un trait d'union. Arabe :
// harakat, hamza, et le tatweel ـ d'allongement.)
const ACCENTS = /[\u0300-\u036f\u0591-\u05bd\u05bf\u05c1\u05c2\u05c4\u05c5\u05c7\u0640\u064b-\u065f\u0670\u06d6-\u06ed\u1ab0-\u1aff\u1dc0-\u1dff\u20d0-\u20ff\ufe20-\ufe2f\p{Default_Ignorable_Code_Point}]/gu;
const pli = (s) => ` ${espaces(s).normalize('NFD').replace(ACCENTS, '').toUpperCase().toLowerCase().replace(/[-']/g, ' ').replace(/\s+/g, ' ').trim()} `;

// Lettres que la décomposition ne simplifie pas (Łukasz, Søren, Đorđe, Æsa, Œuvray,
// et STRAẞER : le ẞ majuscule redevient « ß », alors que « ß » devient « ss »).
// Et ی / ک du clavier persan = ي / ك arabes ; ى (alef maksura, que ی imite en fin de
// mot) = ي aussi.
const SIMPLES = { 'ł': 'l', 'ø': 'o', 'đ': 'd', 'ð': 'd', 'æ': 'ae', 'œ': 'oe', 'þ': 'th', 'ħ': 'h', 'ŧ': 't', 'ß': 'ss', 'ی': 'ي', 'ک': 'ك', 'ى': 'ي' };

/** Les mots d'un nom, pliés comme ci-dessus, sans ponctuation, triés : « Isha-Didier
 *  Freitas », « FREITAS, Isha-Didier » et « Freitas (Isha Didier) » donnent la même
 *  liste ; « Łukasz » = « Lukasz » ; l'apostrophe ne coupe pas (« D'Angelo » =
 *  « DANGELO »). Sert à retrouver un compte existant, et un nom saisi deux fois. */
function motsBruts(s) {
  // Pour comparer seulement : tous les caractères de format, liants compris, et les
  // lettres modificatives du bloc U+02B0–U+02FF (okina ʻ, ʿ ʾ des translittérations
  // arabes, ˈ…) — pas 々, ー ou ๆ, qui font partie du nom —, gardées telles quelles
  // dans le nom enregistré.
  // NFKC : lettres « pleine chasse » (Ｍａｒｉｅ, saisies au clavier asiatique) et
  // ligatures ramenées à leur forme simple. Les signes vocaliques (\p{M}) restent.
  // Tronqué APRÈS NFKC (qui peut multiplier la longueur) : le coût reste borné.
  return pli(espaces(s).normalize('NFKC').slice(0, 420).replace(/[\p{Cf}\p{Default_Ignorable_Code_Point}]/gu, '').replace(/['\u02b0-\u02ff]/g, ''))
    .replace(/[łøđðæœþħŧßیکى]/g, (c) => SIMPLES[c])
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ').trim().split(' ').filter(Boolean);
}
export function motsDuNom(s) {
  return motsBruts(s).sort();
}

/** Ce qu'on compare d'un nom : ses mots triés, et ses mots collés dans l'ordre en
 *  commençant par chacun d'eux (« Tom Van Damme » → tomvandamme, vandammetom,
 *  dammetomvan) — pour reconnaître « VANDAMME Tom », « O NEIL » / « O'Neil »,
 *  « BENALI » / « Ben Ali », « Jeanmarc » / « Jean-Marc ». */
export function formesDuNom(s) {
  const mots = motsBruts(s);
  // Rotations pour un nom d'au plus 12 mots (au-delà, ce n'est plus un nom de personne).
  const rotations = new Set((mots.length <= 12 ? mots : mots.slice(0, 1)).map((_, i) => [...mots.slice(i), ...mots.slice(0, i)].join('')));
  return { mots: [...mots].sort(), ordre: mots, colle: mots.join(''), rotations };
}

/** Deux listes de mots (motsDuNom) : « exact » (mêmes mots), « proche » (l'une
 *  contenue dans l'autre, deux mots au moins de chaque côté), sinon null. Une seule
 *  règle : comptes existants (serveur) et noms saisis deux fois (écran et serveur). */
export function ressemblance(a, b) {
  if (!a.length || !b.length) return null;   // un « nom » sans mot ne ressemble à rien
  if (a.length === b.length && a.every((m, i) => m === b[i])) return 'exact';
  if (a.length < 2 || b.length < 2) return null;
  const inclus = (petit, grand) => {
    const reste = [...grand];
    return petit.every((m) => { const i = reste.indexOf(m); if (i < 0) return false; reste.splice(i, 1); return true; });
  };
  return inclus(a, b) || inclus(b, a) ? 'proche' : null;
}

/** La même question pour deux noms (formesDuNom) : « exact » (mêmes mots), « proche »
 *  (l'un contenu dans l'autre, ou les mêmes lettres collées dans le même ordre —
 *  seulement PROPOSÉ, jamais pré-coché), sinon null. */
export function ressemblanceFormes(fa, fb) {
  const r = ressemblance(fa.mots, fb.mots);
  if (r) return r;
  // Dans les deux sens : « Benali » / « Ali Ben » comme « Ali Ben » / « Benali ».
  return fa.colle && fb.colle && (fa.rotations.has(fb.colle) || fb.rotations.has(fa.colle)) ? 'proche' : null;
}

/** Mêmes mots dans le MÊME ordre, tels qu'écrits (« Ricciardone Arthur » /
 *  « RICCIARDONE Arthur »). Pas à une rotation près : à deux mots, toute inversion en
 *  serait une, et « Ali Mohamed » n'est peut-être pas « MOHAMED Ali ». */
export function dansLeMemeOrdre(fa, fb) {
  return fa.ordre.length > 0 && fa.ordre.length === fb.ordre.length && fa.ordre.every((m, i) => m === fb.ordre[i]);
}

/** Lettres d'un nom après nettoyage (caractères invisibles retirés) — la règle « au
 *  moins 2 lettres » du formulaire public, à l'écran comme au serveur. */
export function lettresDuNom(s) {
  return (espaces(s).match(/\p{L}/gu) || []).length;
}

/** Un nom de personne : au moins 3 caractères, dont 2 lettres (pas « --- », « J.. » ni
 *  un numéro) — la même exigence que le formulaire public. */
export function estUnNom(s) {
  return espaces(s).length >= 3 && (motsBruts(s).join('').match(/\p{L}/gu) || []).length >= 2;
}

/** Toutes les paires qui désignent peut-être la même personne : [i, j, niveau], les
 *  « exact » d'abord. */
export function pairesEnDouble(noms) {
  const formes = noms.map(formesDuNom);
  const exactes = [], proches = [];
  for (let j = 1; j < formes.length; j++) {
    for (let i = 0; i < j; i++) {
      const r = ressemblanceFormes(formes[i], formes[j]);
      if (r === 'exact') exactes.push([i, j, 'exact']);
      else if (r) proches.push([i, j, 'proche']);
    }
  }
  return [...exactes, ...proches];
}

/** Clé d'une paire de noms (sans ordre ni graphie) : « ces deux-là sont bien deux
 *  personnes différentes » est confirmé pour CETTE paire, pas pour toutes. */
export function clePaire(a, b) {
  return [motsDuNom(a).join(' '), motsDuNom(b).join(' ')].sort().join(' | ');
}

/** Message (écran et serveur) pour une telle paire. */
export function messageNomsEnDouble(a, b, niveau = 'exact') {
  const x = espaces(a), y = espaces(b);
  if (niveau !== 'exact') return `« ${x} » et « ${y} » désignent peut-être la même personne.`;
  return (x === y ? `« ${x} » est saisi deux fois` : `« ${x} » et « ${y} » sont le même nom`)
    + ' : retirez le doublon. (Deux personnes différentes portant exactement le même nom : créez la seconde depuis Utilisateurs.)';
}

/**
 * Nom affiché à enregistrer, et s'il est cohérent.
 *  - Nom ET Prénom → déduit (« NOM Prénom »).
 *  - Un seul des deux → le nom saisi, qui doit le contenir ; vide → la partie connue.
 *    (Écraser « Jean Dupont » par « DUPONT » parce que le Prénom manque perdait « Jean ».)
 *  - Aucun → le nom saisi (robots, comptes techniques).
 */
export function nomAEnregistrer(lastName, firstName, nomSaisi) {
  const nom = espaces(lastName), prenom = espaces(firstName), saisi = espaces(nomSaisi);
  if (nom && prenom) return { nom: nomAffiche(nom, prenom), incoherent: false };
  const partie = nom || prenom;
  if (!partie) return { nom: saisi || null, incoherent: false };
  if (!saisi) return { nom: nomAffiche(nom, prenom), incoherent: false };
  return { nom: saisi, incoherent: !pli(saisi).includes(pli(partie)) };
}

/** Le nom (affiché, Nom, Prénom) a-t-il changé ? Comparé APRÈS mise en forme :
 *  un ancien prénom « jean » renvoyé tel quel (devenu « Jean ») n'est pas un changement. */
export function nomsModifies(avant, apres) {
  const pareil = (a, b, forme) => forme(a || '') === forme(b || '');
  return (apres.name !== undefined && !pareil(apres.name, avant.name, nettoyerNom))
    || (apres.lastName !== undefined && !pareil(apres.lastName, avant.lastName, nomEnMajuscules))
    || (apres.firstName !== undefined && !pareil(apres.firstName, avant.firstName, prenomEnForme));
}

/** Message (écran et serveur) quand le nom affiché ne contient pas le Nom / le Prénom. */
export function messageNomIncoherent(saisi, nom, prenom) {
  const n = espaces(nom), p = espaces(prenom);
  const partie = n ? `le Nom « ${n} »` : `le Prénom « ${p} »`;
  return `Le nom affiché « ${espaces(saisi)} » ne contient pas ${partie}. Corrigez l'un des deux, `
    + `ou remplissez aussi ${n ? 'le Prénom' : 'le Nom'} : le nom affiché sera alors calculé tout seul.`;
}
